import React, { useState, useEffect } from "react";
import { Dayjs } from "dayjs";
import TimePicker from "react-time-picker";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

// Locale-aware time formatting
const formatTimeLocale = (date: Date | Dayjs | null | undefined): string => {
  if (!date) return "--:--";
  const d = date instanceof Date ? date : date.toDate();
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Detect if locale uses 12-hour format (has AM/PM)
const uses12HourFormat = (() => {
  const formatted = new Date().toLocaleTimeString([], { hour: 'numeric' });
  return /AM|PM/i.test(formatted);
})();

// Time format based on locale
const timeFormat = uses12HourFormat ? "h:mm a" : "HH:mm";

interface TimeRangePickerProps {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (value: [Dayjs, Dayjs] | null) => void;
  separator?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  value,
  onChange,
  separator = "-",
  className,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);

  // Sync with value prop
  useEffect(() => {
    if (value?.[0]) setStartTime(value[0].format("HH:mm"));
    if (value?.[1]) setEndTime(value[1].format("HH:mm"));
  }, [value]);

  const formatDisplay = (date: Dayjs | null | undefined): string => {
    return formatTimeLocale(date);
  };

  const handleApply = () => {
    if (startTime && endTime && value?.[0] && value?.[1]) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const newStart = value[0].hour(startHours).minute(startMinutes).second(0);
      const newEnd = value[1].hour(endHours).minute(endMinutes).second(0);

      onChange?.([newStart, newEnd]);
    }
    setOpen(false);
  };

  const displayValue = `${formatDisplay(value?.[0])} ${separator} ${formatDisplay(value?.[1])}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm font-medium tabular-nums min-w-[130px]",
            "hover:bg-accent hover:text-accent-foreground transition-colors",
            "focus:outline-none focus:ring-1 focus:ring-border",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          {displayValue}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {/* Start time */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Start</label>
              <div className="time-input-wrapper">
                <TimePicker
                  value={startTime}
                  onChange={(val) => setStartTime(val as string | null)}
                  format={timeFormat}
                  disableClock
                  clearIcon={null}
                  clockIcon={null}
                />
              </div>
            </div>

            <span className="text-muted-foreground pt-5">–</span>

            {/* End time */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">End</label>
              <div className="time-input-wrapper">
                <TimePicker
                  value={endTime}
                  onChange={(val) => setEndTime(val as string | null)}
                  format={timeFormat}
                  disableClock
                  clearIcon={null}
                  clockIcon={null}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="link" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="segment" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
